// 2) Third-party library imports
import { createAction, createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';

// 3d) Utility functions
import { DynamicObject } from '@utils/commonInterfaces';

// 3e) Redux actions & types
import { AuthState, VerifyMfaSoftwareTokenPayload } from './types';
import type { RootState } from '../storeTypes';

const actionPrefix = 'auth';
export const initialState: AuthState = {
	currentUser: null,
	isAuth: false,
	regionSettingsConfig: [],
	regionKeyConfig: [],
	identityProviderConfig: [],
};

const authSlice = createSlice({
	name: 'auth',
	initialState,
	reducers: {
		loginUserAction: (state, action: PayloadAction<DynamicObject>) => {
			state.currentUser = action.payload['currentUser'];
			state.isAuth = action.payload['isAuth'];
		},
		logoutUserAction: (_state) => {
			return initialState;
		},
		updateRegionSettingsConfigAction: (state, action: PayloadAction<DynamicObject[]>) => {
			state.regionSettingsConfig = action.payload;
		},
		updateRegionKeyConfigAction: (state, action: PayloadAction<DynamicObject[]>) => {
			state.regionKeyConfig = action.payload;
		},
		updateIdentityProviderConfigAction: (state, action: PayloadAction<DynamicObject[]>) => {
			state.identityProviderConfig = action.payload;
		}
	},
});

const { actions, reducer } = authSlice;

// Actions
export const {
	loginUserAction,
	logoutUserAction,
	updateRegionSettingsConfigAction,
	updateRegionKeyConfigAction,
	updateIdentityProviderConfigAction
} = actions;
export const getRegionSettingsConfigAction = createAction(`${actionPrefix}getRegionSettingsConfigAction`);
export const getRegionKeyConfigAction = createAction(`${actionPrefix}getRegionKeyConfigAction`);
export const generateMfaSoftwareTokenAction = createAction(`${actionPrefix}generateMfaSoftwareTokenAction`, 
	function prepare(callback: (response: DynamicObject) => void) {
		return {
			payload: {
				callback
			}
		};
	}
);
export const verifyMfaSoftwareTokenAction = createAction(`${actionPrefix}verifyMfaSoftwareTokenAction`,
	function prepare(payload: VerifyMfaSoftwareTokenPayload, callback: (response: DynamicObject) => void) {
		return {
			payload: {
				payload,
				callback
			}
		};
	}
);
export const useSmsMfaAction = createAction(`${actionPrefix}useSmsMfaAction`, 
	function prepare(callback: (response: DynamicObject) => void) {
		return {
			payload: { callback }
		};
	}
);
export const changeEndpointsAction = createAction(`${actionPrefix}changeEndpointsAction`,
	function prepare(companyUserRoleData: DynamicObject) {
		return {
			payload: companyUserRoleData
		};
	}
);
export const getIdentityProviderConfigAction = createAction(`${actionPrefix}getIdentityProviderConfigAction`);
export const resetReduxStateAction = createAction(`${actionPrefix}resetReduxStateAction`);

// Selectors
export const getAuthState = (state: RootState) => state.auth;
export const getIsAuth = createSelector(
	getAuthState,
	(auth) => auth.isAuth
);
export const getCurrentUser = createSelector(
	getAuthState,
	(auth) => auth.currentUser
);
export const getRegionSettingsConfig = createSelector(
	getAuthState,
	(auth) => auth.regionSettingsConfig
);
export const getRegionKeyConfig = createSelector(
	getAuthState,
	(auth) => auth.regionKeyConfig
);
export const getIdentityProviderConfig = createSelector(
	getAuthState,
	(auth) => auth.identityProviderConfig
);

// Reducer
export default reducer;